/* eslint-disable no-nested-ternary */
import { Fragment, createRef, useEffect, useRef, useState } from "react";
import styles from "./SectionNavigation.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { sleep } from "@Components/LeadFormModal/utils";
import throttle from "@Utils/throttle";
import NavItem from "./SectionNavigationItem/SectionNavigationItem";
import { logger } from "helpers/logger";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import useWhitelabel from "hooks/useWhitelabel";
import useIsApp from "hooks/useIsApp";
import useIsDesktop from "hooks/useIsDesktop";
import { useHistory } from "hooks/useHistory";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE, CLICKED_ITEM } from "@Utils/webApputils/analyticEvents";

const SectionNavigation = (props: {
  globalTopOffset?: number;
  navigationData: Array<{
    id: string;
    displayName: string;
    triggerInViewEvent?: boolean;
  }>;
  addSticky?: boolean;
  addRelative?: boolean;
  wrapperRef?: any;
  addStatic?: boolean;
  tabType?: "underline" | "background";
  onTabClick?: any;
  currTab?: string;
  showIcon?: boolean;
  dataTestId?: string;
  gtagEvent?: string;
  isLazyElementsPrecent?: boolean;
  trottleOffset?: number;
  zIndex?: any;
  largeElement?: boolean;
  isMapViewEnabled?: boolean;
  listItemClassName?: string;
  activelistItemClassName?: string;
  wrapperClassName?: string;
  childView?: boolean;
  currentTabBehaviour?: string;
}) => {
  const {
    globalTopOffset = 0, // height of navbar
    navigationData = [],
    addSticky = false,
    addRelative = false,
    addStatic = false,
    wrapperRef = null,
    tabType = "underline",
    onTabClick = null,
    currTab = "",
    showIcon = false,
    dataTestId = "",
    gtagEvent = "",
    // isLazyElementsPrecent = false,
    trottleOffset = 400,
    zIndex = null,
    largeElement = false,
    isMapViewEnabled = false,
    listItemClassName = "",
    activelistItemClassName = "",
    wrapperClassName = "",
    childView = false,
    currentTabBehaviour = "",
  } = props;
  // Separate refs for NavItem <li> elements, used for tab bar centering
  const navItemRef = useRef<any>(
    navigationData.reduce((acc: any, it) => {
      acc[it.id] = createRef();
      return acc;
    }, {}),
  );
  // Refs for parent child-wrapper divs (used for centering when NavItem is hidden)
  const parentDivRef = useRef<any>(
    navigationData.reduce((acc: any, it) => {
      acc[it.id] = createRef();
      return acc;
    }, {}),
  );
  const [activeTab, setActiveTab] = useState("");
  const containerRef = useRef<any>(null);
  const prevScrollTopRef = useRef(0);
  const manualTabClickRef = useRef(false);
  const isApp = useIsApp();
  const isDesktop = useIsDesktop();
  const [filteredNavigationData, setFilteredNavigationData] = useState<any>([]);
  const [removeIcon, setRemoveIcon] = useState(false);
  const isMiniProgram = useIsMiniProgram();
  const { t } = useTranslationHook();
  const { isWhitelabel } = useWhitelabel();
  const history = useHistory();

  const handleScrollIntoViewWithOffset = (
    idx: string,
    behavior = "smooth",
    topOffset = globalTopOffset,
  ) => {
    const element = document.getElementById(idx);
    if (!element) return;
    const elementPosition = element?.getBoundingClientRect().top;
    const isScrollingUp = elementPosition < 0;
    const headerOffset = !isDesktop && isScrollingUp ? 102 : topOffset + 48 + 8; // add extra height to show heading properly P1D-5154
    const yOffset = wrapperRef?.current ? wrapperRef?.current?.scrollTop : window.pageYOffset;
    let offsetPosition = elementPosition + yOffset - headerOffset;

    if (showIcon) {
      if (wrapperRef?.current?.scrollTop <= 100) {
        offsetPosition -= 32;
      } else {
        offsetPosition -= 8;
      }
    }

    // const handleOnScrollEnd = () => {
    //   window.manualScrollTrigger = false;
    //   if (!element) return;
    //   if (
    //     !(
    //       Math.abs(element?.getBoundingClientRect().top) < headerOffset + 10 &&
    //       Math.abs(element?.getBoundingClientRect().top) > headerOffset - 10
    //     )
    //   ) {
    //     handleScrollIntoViewWithOffset(idx, behavior, topOffset);
    //   }
    //   (wrapperRef?.current || window).removeEventListener("scrollend", handleOnScrollEnd);
    // };

    if (behavior == "smooth") {
      // On Windows, smooth scroll takes much longer to complete and the layout-shift
      // correction fires mid-animation, causing wrong positioning. Use instant scroll
      // on Windows so the position is immediately correct, then only apply the
      // correction if needed (for lazy-loaded sections that shift layout).
      const isWindows =
        typeof navigator !== "undefined" && /Win/.test(navigator.platform || navigator.userAgent);
      const scrollBehavior = isWindows ? "auto" : "smooth";

      (wrapperRef?.current || window).scrollTo({
        top: offsetPosition,
        behavior: scrollBehavior,
      });
      // After scroll completes, verify position and correct if layout shifted.
      if ((window as any).__sectionNavCorrectionTimer) {
        clearTimeout((window as any).__sectionNavCorrectionTimer);
      }
      // On Windows (instant scroll) the correction can run sooner since there's no animation.
      const correctionDelay = isWindows ? 100 : 800;
      (window as any).__sectionNavCorrectionTimer = setTimeout(() => {
        (window as any).__sectionNavCorrectionTimer = null;
        const el = document.getElementById(idx);
        if (!el) return;
        const currentTop = el.getBoundingClientRect().top;
        const freshIsScrollingUp = currentTop < 0;
        const freshHeaderOffset = !isDesktop && freshIsScrollingUp ? 102 : topOffset + 48 + 8; // add extra height to show heading properly P1D-5154
        if (Math.abs(currentTop - freshHeaderOffset) > 10) {
          const correctedPosition =
            currentTop +
            (wrapperRef?.current ? wrapperRef.current.scrollTop : window.pageYOffset) -
            freshHeaderOffset;
          (wrapperRef?.current || window).scrollTo({
            top: correctedPosition,
            behavior: scrollBehavior,
          });
        }
      }, correctionDelay);
      window.manualScrollTrigger = false;
    } else {
      (wrapperRef?.current || window).scrollBy(0, element.offsetTop - topOffset);
    }
  };

  const handleOnTabClick = (id: string, name = "") => {
    onTabClick?.(id);
    if (isApp || isMiniProgram) {
      manualTabClickRef.current = true;
      setActiveTab(id);
    }
    handleScrollIntoViewWithOffset(id);
    if (name === "Map") {
      logAppEvent({
        eventName: APP_EVENTS.CTA_CLICKED,
        payload: {
          cta_name: CLICKED_ITEM.VIEW_MAP,
          screen_name: APP_PAGE.PROPERTY,
        },
      });
    }
    gtagEventEmitter({
      action: gtagEvent,
      category: PAGE.INVENTORY,
      label: "",
      tab_name: name,
    });
  };
  const handleOnScroll = throttle(() => {
    let active = "";
    const offset = window.innerHeight * 0.3;

    const currOffset = wrapperRef?.current ? wrapperRef.current.scrollTop : window.pageYOffset;
    const prevOffset = prevScrollTopRef.current;
    const isScrollingDown = currOffset > prevOffset;
    prevScrollTopRef.current = currOffset;

    try {
      if (currOffset >= 0) {
        // Show/hide icon after scrolling a certain distance
        if (showIcon && wrapperRef?.current?.scrollTop) {
          setRemoveIcon(wrapperRef?.current?.scrollTop > 100);
        }

        // Determine active section based on scroll
        // A section is active only while it's still visible in the viewport
        navigationData.forEach((it) => {
          const element = document.getElementById(it.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top < offset && rect.bottom > 0) {
              active = it.id;
            }
          }
        });

        if (isApp || isMiniProgram) {
          const lastId = navigationData[navigationData?.length - 1]?.id;
          // Case 1: Prevent override when scrolling down at manual click
          if (isScrollingDown && manualTabClickRef.current) return;
          // Case 2: Check if scrolled to the very bottom
          const scrollHeight = wrapperRef?.current
            ? wrapperRef.current.scrollHeight
            : document.documentElement.scrollHeight;
          const clientHeight = wrapperRef?.current
            ? wrapperRef.current.clientHeight
            : window.innerHeight;

          if (currOffset + clientHeight >= scrollHeight - 5 && !manualTabClickRef.current) {
            active = lastId || active; // Auto-activate last tab if not manual click
          }
          // Reset manual tab click flag when scrolling up
          if (manualTabClickRef.current && !isScrollingDown) manualTabClickRef.current = false;
        }
        // Update active tab if it changed
        if (active !== activeTab) setActiveTab(active);
      }
    } catch (err) {
      console.error(err);
    }
  }, trottleOffset);

  useEffect(() => {
    if (activeTab) {
      const activeNavData = filteredNavigationData.find((it: any) => it.id === activeTab);
      const hasChild = !!activeNavData?.child;
      // When a tab has children, the <li> is hidden (display:none) so offsetLeft=0.
      // Use the parent wrapper div ref instead, which is the visible flex item.
      const elementRef = hasChild
        ? parentDivRef.current[activeTab]?.current
        : navItemRef.current[activeTab]?.current;
      const containerWidth = containerRef?.current?.offsetWidth;
      const elementWidth = elementRef?.offsetWidth;
      let scrollLeft;
      if (hasChild && elementWidth > containerWidth) {
        // Child wrapper is wider than the container — show preceding visible tab(s) to the left
        // so the pill doesn't appear stuck to the edge (keeps context of navigation position)
        let prevVisible = elementRef?.previousElementSibling;
        // Skip hidden elements (the hidden <li> for the active parent tab itself)
        while (prevVisible && (prevVisible as HTMLElement).offsetWidth === 0) {
          prevVisible = prevVisible.previousElementSibling;
        }
        if (prevVisible) {
          scrollLeft = Math.max(0, (prevVisible as HTMLElement).offsetLeft - 16);
        } else {
          scrollLeft = Math.max(0, elementRef?.offsetLeft - 16);
        }
      } else {
        scrollLeft = elementRef?.offsetLeft - (containerWidth - elementWidth) / 2;
      }
      containerRef?.current?.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });

      // For child views: also scroll the parent scrollable container to keep the
      // active child tab visible as user scrolls through content
      if (childView && elementRef) {
        const parentScrollable = containerRef?.current?.parentElement?.parentElement;
        if (parentScrollable && parentScrollable.scrollWidth > parentScrollable.clientWidth) {
          const parentContainerWidth = parentScrollable.offsetWidth;
          const tabRect = elementRef.getBoundingClientRect();
          const parentRect = parentScrollable.getBoundingClientRect();
          const tabRelativeLeft = tabRect.left - parentRect.left + parentScrollable.scrollLeft;
          const tabCenter = tabRelativeLeft + tabRect.width / 2;
          const targetScroll = tabCenter - parentContainerWidth / 2;
          parentScrollable.scrollTo({
            left: Math.max(0, targetScroll),
            behavior: "smooth",
          });
        }
      }

      const hash = window.location.hash?.split("#")?.[1] || "";
      if (hash && activeTab !== hash) {
        const cleanURL = `${window.location.pathname}${window.location.search}`;
        history.replace(cleanURL);
      }
    }
    filteredNavigationData.forEach((it: any) => {
      if (it?.triggerInViewEvent) {
        if (it?.id === activeTab) {
          const event = new Event(`${it.id}_section_in_view`);
          (wrapperRef?.current || window).dispatchEvent(event);
        } else {
          const event = new Event(`${it.id}_section_not_in_view`);
          (wrapperRef?.current || window).dispatchEvent(event);
        }
      }
    });
    (wrapperRef?.current || window).addEventListener("scroll", handleOnScroll);
    return () => {
      (wrapperRef?.current || window).removeEventListener("scroll", handleOnScroll);
    };
  }, [activeTab]);

  const handleSectionsRemoved = () => {
    const data = navigationData
      .filter((it) => {
        const element = document.getElementById(it?.id);
        return !!element;
      })
      .filter((it) => (isWhitelabel ? it?.id !== "similar_properties" : true));

    setFilteredNavigationData(data);
  };

  const handleNavigateToSectionEvent = (e: any) => {
    if (e?.detail?.id) {
      handleScrollIntoViewWithOffset(e?.detail?.id);
    }
  };

  useEffect(() => {
    handleSectionsRemoved();
    if (currTab) {
      const isFirstTab = currTab === navigationData?.[0]?.id;
      if (isFirstTab) {
        // First section is already visible at the top — just highlight the tab, no scroll needed.
        setTimeout(() => setActiveTab(currTab));
      } else if (currentTabBehaviour) {
        setTimeout(() => {
          handleScrollIntoViewWithOffset(currTab, currentTabBehaviour);
        }, 500);
        setTimeout(() => setActiveTab(navigationData?.[0]?.id || currTab));
      } else {
        handleScrollIntoViewWithOffset(currTab, "auto");
        setTimeout(() => setActiveTab(currTab));
      }
    }
    window.addEventListener("navigate_to_section", handleNavigateToSectionEvent);
    window.addEventListener("navigation_section_removed", handleSectionsRemoved);
    return () => {
      window.removeEventListener("navigation_section_removed", handleSectionsRemoved);
      window.removeEventListener("navigate_to_section", handleNavigateToSectionEvent);
    };
  }, []);

  useEffect(() => {
    handleSectionsRemoved();
  }, [navigationData]);

  useEffect(() => {
    try {
      const hash = window.location.hash?.split("#")?.[1];
      const hashTab = navigationData.find((it) => it.id === hash)?.id;
      if (hashTab) {
        (async () => {
          await sleep(100);
          handleScrollIntoViewWithOffset(hashTab || "");
        })();
      }
    } catch (err) {
      logger.error(err);
    }
  }, []);

  if (!filteredNavigationData?.length) {
    return <></>;
  }
  let display = activeTab ? "flex" : "none";
  const largeAllowedTabs = [
    isMapViewEnabled ? "map" : null,
    "reviews",
    "price_trend",
    "similar_properties",
    "nearby_properties",
    "popular_properties",
    "budget_friendly_properties",
  ];
  if (largeElement) {
    display = largeAllowedTabs.includes(activeTab) ? "flex" : "none";
  }
  return (
    <ul
      ref={containerRef}
      className={formatClassNames(
        styles.container,
        !activeTab && styles.hide,
        addSticky && styles.addSticky,
        addRelative && styles.addRelative,
        addStatic && styles.addStatic,
        tabType && styles[`${tabType}Tab`],
        wrapperClassName,
        childView && styles.childView,
      )}
      style={{ top: globalTopOffset, zIndex, display }}
    >
      {filteredNavigationData?.map((it: any) => (
        <Fragment key={it?.id}>
          {it?.child && (
            <div
              ref={parentDivRef.current[it.id]}
              style={activeTab !== it?.id ? { display: "none" } : {}}
              className={styles.parent}
            >
              <SectionNavigation
                {...props}
                navigationData={it.child}
                wrapperClassName=""
                listItemClassName=""
                activelistItemClassName=""
                addStatic
                childView
                currTab=""
                currentTabBehaviour=""
              />
            </div>
          )}
          {isMiniProgram && it?.displayName === "3D" ? null : (
            <NavItem
              key={it?.id}
              dataTestId={dataTestId}
              listRef={navItemRef.current[it.id]}
              activeTab={activeTab === it?.id}
              it={it}
              displayName={t(it?.displayName)}
              handleOnTabClick={handleOnTabClick}
              showIcon
              removeIcon={removeIcon}
              className={formatClassNames(
                styles.listItem,
                listItemClassName,
                activeTab === it.id && styles.active,
                activeTab === it.id && activelistItemClassName,
                it?.child && activeTab === it.id && styles.hidden,
              )}
            />
          )}
        </Fragment>
      ))}
    </ul>
  );
};

export default wrapperHOC(SectionNavigation, {
  componentName: "SectionNavigation-Global",
  showForChina: true,
});
